import {useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {CREATE_AGENT_REFERRAL_INVITATION} from '../../../gql/mutations/agentOnboardingMutations'

interface IModalInviteReferredProps {
  isOpen: boolean
  onClose: () => void
}

const validationSchema = Yup.object({
  email: Yup.string().email('Invalid email format').required('Email is required'),
})

export const ModalInviteReferred: React.FC<IModalInviteReferredProps> = ({isOpen, onClose}) => {
  const [createInvitation, {loading}] = useMutation(CREATE_AGENT_REFERRAL_INVITATION)
  const [fallbackLink, setFallbackLink] = useState<string | null>(null)

  const handleClose = () => {
    setFallbackLink(null)
    formik.resetForm()
    onClose()
  }

  const formik = useFormik({
    initialValues: {email: ''},
    validationSchema,
    onSubmit: async (values, {resetForm}) => {
      try {
        const {data} = await createInvitation({variables: {email: values.email}})
        const result = data?.createAgentReferralInvitation
        if (result?.success) {
          if (result.emailSent) {
            toast.success(`Invitation sent to ${values.email}`)
            resetForm()
            onClose()
          } else {
            toast.warning(
              'Invitation created, but the email could not be sent. Share this link manually.'
            )
            setFallbackLink(result.inviteUrl)
          }
        } else {
          toast.error(result?.errors?.[0]?.message || 'Could not create the invitation.')
        }
      } catch (err: any) {
        toast.error(err?.message || 'Could not create the invitation.')
      }
    },
  })

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Invite Referred Agent</Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Email</Form.Label>
            <Form.Control
              type='email'
              name='email'
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.touched.email && !!formik.errors.email}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
          </Form.Group>
          {fallbackLink && (
            <Form.Group className='mt-3'>
              <Form.Label>Invitation link</Form.Label>
              <Form.Control readOnly value={fallbackLink} onFocus={(e) => e.target.select()} />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant='primary' type='submit' disabled={loading}>
            {loading ? <Spinner animation='border' size='sm' /> : 'Send Invitation'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
